import React, { useEffect, useState } from "react";
import { textoDuracion } from "../demos/configCallMessages";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import { CalendarClock, Settings, Loader2, Eye } from "lucide-react";
import axiosClient from "../../api/axiosClient";
import Time24Picker from "../ui/Time24Picker";
import { scheduleDemo } from "../../api/demosApi";
import { capitalizeName } from "../../utils/displayName";
import { buildScheduleSummary } from "../demos/scheduleSummary";
import ConfigCallPreviewModal from "./ConfigCallPreviewModal";
import { jwtDecode } from "jwt-decode";

const SUPPORT_PHONE = "+34 960 600 024";

export default function ScheduleDemoBlock({ lead, onScheduled, kind = "demo", simulated = false }) {
  const { t } = useTranslation();
  const [closers, setClosers] = useState([]);
  const [closer, setCloser] = useState("");
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("10:00");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  // Duración de la llamada de configuración (20/10/2026): 30 min de siempre o más si sabemos que se alargará.
  const [duracion, setDuracion] = useState(30);

  useEffect(() => {
    if (simulated) return;
    (async () => {
      try {
        const r = await axiosClient.get("/usuarios");
        const list = (Array.isArray(r) ? r : r?.data || []);
        const onlyClosers = list.filter((u) => u.televenta_role === "closer");
        const pool = onlyClosers.length ? onlyClosers : list.filter((u) => Number(u.level) >= 1);
        setClosers(pool);
        // Por defecto, quien está usando la app si es closer (David y Trini lo son, 4/10/2026); si no, el primero.
        let me = null;
        try { const tok = localStorage.getItem("token"); if (tok) me = jwtDecode(tok).user_id || null; } catch { /* sin sesión legible */ }
        const mine = pool.find((u) => u.user_id === me);
        if (pool.length) setCloser((mine || pool[0]).user_id);
      } catch (e) { /* no-fatal */ }
    })();
  }, [simulated]);

  const submit = async () => {
    setErr("");
    if (!closer || !date || !time) { setErr("Elige closer, fecha y hora."); return; }
    const [hh, mm] = time.split(":");
    const dt = new Date(date); dt.setHours(Number(hh), Number(mm), 0, 0);
    if (simulated) {
      // Simulador de producto: sin backend, no-op local (mismo patrón que ActiveDemoCard).
      onScheduled?.();
      return;
    }
    setSaving(true);
    try {
      const out = await scheduleDemo({
        lead_contact_id: lead.contact_id, lead_nombre: lead.nombre,
        lead_telefono: lead.telefono, sector: lead.sector,
        closer, scheduled_at: dt.toISOString(),
        ...(kind === "config" ? { call_kind: "config", ...(duracion !== 30 ? { duration_min: duracion } : {}) } : {}),
      });
      // Agendar ya no solo escribe la demo: bloquea el hueco en el calendario y programa
      // los recordatorios del lead. Se cuenta para que no haya que explicarlo después.
      window.alert(buildScheduleSummary(out, t, kind));
      onScheduled?.(out?.demo || out);
    } catch (e) {
      setErr(e?.response?.data?.error || "No se pudo agendar.");
    } finally { setSaving(false); }
  };

  const isConfig = kind === "config";
  const Icon = isConfig ? Settings : CalendarClock;

  // Vista previa (demo Y config): usa lo que el admin tiene elegido AHORA MISMO — closer del
  // pool, fecha/hora (mañana 10:00 de ejemplo si aún no eligió) y el teléfono real del closer
  // (fallback soporte), igual que resolverá el backend al agendar.
  const selectedCloser = closers.find((u) => u.user_id === closer);
  const previewDate = date || new Date(Date.now() + 86400000);
  const previewTime = date ? time : "10:00";
  const previewVals = {
    nombre: lead?.nombre || "Cliente",
    dateEs: new Intl.DateTimeFormat("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" }).format(previewDate),
    timeStr: previewTime,
    asesor: selectedCloser ? (selectedCloser.full_name || capitalizeName(selectedCloser.user_id)) : "tu asesor",
    telefono: selectedCloser?.phone || SUPPORT_PHONE,
    ...(isConfig && duracion !== 30 ? { duracion: textoDuracion(duracion) } : {}),
  };

  return (
    <div className={`mt-3 p-3 rounded-2xl border ${isConfig ? "border-sky-200 bg-sky-50/50" : "border-emerald-200 bg-emerald-50/50"}`}>
      <div className={`flex items-center justify-between gap-2 mb-2`}>
        <div className={`flex items-center gap-2 font-black text-xs uppercase tracking-wider ${isConfig ? "text-sky-700" : "text-emerald-700"}`}>
          <Icon size={16} /> {isConfig ? "Agendar llamada de configuración" : "Agendar demo al closer"}
        </div>
        <button
          type="button"
          onClick={() => setPreviewOpen(true)}
          title="Ver cómo le llegará al lead"
          className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition"
        >
          <Eye size={15} />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <select value={closer} onChange={(e) => setCloser(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm">
          {closers.map((u) => <option key={u.user_id} value={u.user_id}>{u.full_name || capitalizeName(u.user_id)}</option>)}
        </select>
        <DatePicker selected={date} onChange={setDate} locale="es" dateFormat="dd/MM/yyyy" placeholderText="Fecha" className="border rounded-lg px-2 py-1.5 text-sm w-full" />
        <Time24Picker value={time} onChange={(e) => setTime(e.target.value)} />
      </div>
      {isConfig && (
        <label className="mt-2 flex items-center gap-2 text-sm text-gray-700">
          Duración
          <select value={duracion} onChange={(e) => setDuracion(Number(e.target.value))} className="border rounded-lg px-2 py-1.5 text-sm" data-testid="config-duracion">
            {[30, 45, 60, 90, 120].map((m) => <option key={m} value={m}>{textoDuracion(m).replace("unos ", "")}</option>)}
          </select>
          <span className="text-xs text-gray-500">Bloquea ese tiempo en tu agenda y se lo decimos al cliente.</span>
        </label>
      )}
      {err && <p className="text-red-600 text-xs mt-2">{err}</p>}
      <ConfigCallPreviewModal open={previewOpen} onClose={() => setPreviewOpen(false)} vals={previewVals} kind={kind} />
      <button
        type="button"
        onClick={submit}
        disabled={saving}
        className={`mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-sm font-bold disabled:opacity-50 ${isConfig ? "bg-sky-600" : "bg-emerald-600"}`}
      >
        {saving ? <Loader2 size={15} className="animate-spin" /> : <Icon size={15} />} {isConfig ? "Agendar configuración" : "Agendar demo"}
      </button>
    </div>
  );
}
